import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const ProjectContext = createContext({});

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within ProjectProvider');
  }
  return context;
};

const TOOL_NAME_MAP = {
  '/sketch-to-interior-render': 'Sketch to 3D Interior',
  '/moodboard-to-3d-room': 'Moodboard to 3D Room',
  '/cad-space-planner': 'CAD Space Planner',
  '/cad-based-mood-room-generator': 'CAD-Based Mood Room',
  '/combine-interior-styles': 'Combine Interior Styles',
  '/furniture-editing': 'Furniture Editing',
  '/room-variation-generator': 'Room Variation Generator',
  '/complete-room-design': 'Complete Room Design',
  '/freestyle-field': 'Freestyle Field',
  '/site-concept': 'Site Concept',
};

const FOLDERS_KEY = 'interior_design_folders';

function loadFromStorage(key, fallback = []) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState(() => loadFromStorage(FOLDERS_KEY));
  const [saveModalState, setSaveModalState] = useState(null);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  // Persist folders to localStorage
  useEffect(() => {
    try { localStorage.setItem(FOLDERS_KEY, JSON.stringify(projects)); } catch {}
  }, [projects]);

  // Load uploaded media from Supabase Storage on mount
  useEffect(() => {
    const loadUploadedMedia = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: files } = await supabase.storage
        .from('upload-images')
        .list(user.id, { sortBy: { column: 'created_at', order: 'desc' } });

      if (files && files.length > 0) {
        const urls = files.map((file) => {
          const { data } = supabase.storage
            .from('upload-images')
            .getPublicUrl(`${user.id}/${file.name}`);
          return data.publicUrl;
        });
        setUploadedMedia(urls);
      }
    };
    loadUploadedMedia();
  }, []);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const addUploadedMedia = useCallback(async (files) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      const names = oversized.map((f) => f.name).join(', ');
      alert(`File size exceeds 10MB limit: ${names}`);
      files = files.filter((f) => f.size <= MAX_FILE_SIZE);
      if (files.length === 0) return;
    }

    setUploadingMedia(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const ext = file.name.split('.').pop();
        const filePath = `${user.id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

        const { error } = await supabase.storage
          .from('upload-images')
          .upload(filePath, file, { cacheControl: '31536000', upsert: false });

        if (error) {
          console.error('Upload error:', error.message);
          continue;
        }

        const { data } = supabase.storage.from('upload-images').getPublicUrl(filePath);
        newUrls.push(data.publicUrl);
      }
      if (newUrls.length > 0) {
        setUploadedMedia((prev) => [...newUrls, ...prev]);
      }
    } finally {
      setUploadingMedia(false);
    }
  }, []);

  const removeUploadedMedia = useCallback(async (index) => {
    const url = uploadedMedia[index];
    if (!url) return;

    // Extract file path from public URL
    const match = url.match(/upload-images\/(.+?)(?:\?|$)/);
    if (match) {
      const { error } = await supabase.storage.from('upload-images').remove([match[1]]);
      if (error) console.error('Delete error:', error.message);
    }

    setUploadedMedia((prev) => prev.filter((_, i) => i !== index));
  }, [uploadedMedia]);

  const createProject = useCallback((name) => {
    const newProject = {
      id: Date.now().toString(),
      name,
      usedTools: [],
      images: [],
      createdAt: new Date().toISOString(),
    };
    setProjects((prev) => [newProject, ...prev]);
    return newProject;
  }, []);

  const deleteProject = useCallback((projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
  }, []);

  const renameProject = useCallback((projectId, newName) => {
    setProjects((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, name: newName } : p))
    );
  }, []);

  const saveImageToProject = useCallback((projectId, imageUrl, toolPath) => {
    const toolName = TOOL_NAME_MAP[toolPath] || toolPath;
    setProjects((prev) =>
      prev.map((p) => {
        if (p.id !== projectId) return p;
        const updatedTools = p.usedTools.includes(toolName)
          ? p.usedTools
          : [...p.usedTools, toolName];
        return {
          ...p,
          usedTools: updatedTools,
          images: [...p.images, { url: imageUrl, tool: toolName, savedAt: new Date().toISOString() }],
        };
      })
    );
  }, []);

  const showSaveModal = useCallback((imageUrl, toolPath) => {
    const toolName = TOOL_NAME_MAP[toolPath] || toolPath;
    setSaveModalState({ imageUrl, toolPath, toolName });
  }, []);

  const closeSaveModal = useCallback(() => {
    setSaveModalState(null);
  }, []);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        createProject,
        deleteProject,
        renameProject,
        saveImageToProject,
        saveModalState,
        showSaveModal,
        closeSaveModal,
        uploadedMedia,
        addUploadedMedia,
        removeUploadedMedia,
        uploadingMedia,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export default ProjectContext;
